import { FC, ReactNode } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  HStack,
  Stack,
  Text,
  useStyleConfig,
} from '@chakra-ui/react';

type ToastNotificationSize = 'full-width';
type ToastNotificationVariant = 'default' | 'warning' | 'success' | 'error';
type ToastNotificationPanel = {
  ctaLabel: string;
  body: ReactNode | string;
};

export type ToastNotificationProps = {
  title: string;
  /**
   * Will be displayed on the left side of the title
   */
  icon?: ReactNode;
  subtitle?: string;
  /**
   * `full-width` - takes up the entire width of the viewport
   */
  size?: ToastNotificationSize;
  /**
   * Sets the theme of the toast
   */
  variant?: ToastNotificationVariant;
  /**
   * Sets an expandable panel on the bottom of the toast
   * `ctaLabel` - label for the panel trigger <br>
   * `body` - the content of the panel
   */
  panel?: ToastNotificationPanel;
};

/**
 * A toast notification, when triggered, will display a notification on the bottom of the viewport.
 * It will self-dismiss after five seconds by default.
 * <br>
 * **Prerequisite:**
 * Ensure your app's theme is extended with `chakraTheme` (also exported by this package) before using this component.
 * <br>
 * **Usage:**
 * Set as the `render` value of the [useToast](https://chakra-ui.com/docs/feedback/toast) hook from Chakra.
 */
export const ToastNotification: FC<ToastNotificationProps> = ({
  variant,
  size,
  icon,
  panel,
  title,
  subtitle,
  ...rest
}: ToastNotificationProps) => {
  const styles = useStyleConfig('ToastNotification', { variant, size });

  return (
    <Box __css={styles} {...rest}>
      <Accordion allowMultiple>
        <AccordionItem border="none">
          <Stack>
            <HStack>
              {icon && <Flex paddingRight={2}>{icon}</Flex>}
              <Flex flex={1} direction="column">
                <Flex
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Text noOfLines={1} fontWeight="bold" data-testid="title">
                      {title}
                    </Text>
                  </Box>
                  {panel && (
                    <Box>
                      <AccordionButton
                        height="1rem"
                        justifyContent="end"
                        style={{ fontSize: 'inherit' }}
                      >
                        {panel.ctaLabel}
                        <AccordionIcon ml={1} />
                      </AccordionButton>
                    </Box>
                  )}
                </Flex>
                {subtitle && <Text data-testid="subtitle">{subtitle}</Text>}
              </Flex>
            </HStack>
          </Stack>
          {panel && <AccordionPanel pb={4}>{panel.body}</AccordionPanel>}
        </AccordionItem>
      </Accordion>
    </Box>
  );
};
